import Overlay from './Overlay.js';
import { el } from '../dom.js';
import { SETTINGS } from '../../core/SimConfig.js';
import { load as loadSettings, PERSISTED_KEYS } from '../../core/UserSettings.js';
import { SCHEME_INFO, SCHEME_ORDER } from '../../input/ControlScheme.js';
import { findDevice, deviceLabel, AxisCalibrator } from '../../input/DeviceInput.js';
import {
  toggleFullscreen, isFullscreen, keyboardLockSupported, onKeyboardLockChange,
} from '../../input/keyboardLock.js';

const DEVICE_POLL_MS = 400;

/**
 * Settings overlay — Gameplay + Graphics options. Reads its initial control
 * state from the saved settings (defaults where nothing is stored) and reports
 * every change through `onChange(key, value)`, which App routes into its live
 * settings store and persists.
 *
 * The overlay is built once and shared across the start screen and pause menu;
 * it holds no game references, so it stays valid across scenario rebuilds.
 */
export default class SettingsOverlay extends Overlay {
  constructor({ onClose, onChange, onClearData }) {
    super({ title: 'SETTINGS', onClose });
    this.onChange = onChange;
    this.onClearData = onClearData;

    // Local working copy for the controls' "active" state. App holds the
    // authoritative live values; both are seeded from the same saved settings.
    this.values = loadSettings();

    // Per-control resync closures, collected as the sections are built, so
    // resetToDefaults() can push new values back into the DOM in one sweep.
    this._refreshers = [];

    this._buildGameplay();
    this._buildControls();
    this._buildAudio();
    this._buildGraphics();
    this._buildData();
    this._buildConfirm();
  }

  _emit(key, value) {
    this.values[key] = value;
    this.onChange?.(key, value);
  }

  // ── Gameplay ────────────────────────────────────────────────────────────────
  _buildGameplay() {
    const section = this._section('Gameplay');

    // NO HUD — reversed sense so the control reads as "On = HUD hidden". Locked
    // during combat scenarios, where NO HUD is an armed mission modifier fixed at
    // launch (it grants the NO HUD score bonus) rather than a live toggle.
    const hud = this._segRow(section, 'HUD',
      [{ value: true, label: 'Off' }, { value: false, label: 'On' }],
      () => !this.values.hud,
      (noHud) => this._emit('hud', !noHud));
    this._hudGroup = hud.group;
    this._hudRefresh = hud.refresh;
    this._hudNote = el('div', 'settings-note');
    section.appendChild(this._hudNote);
    this._syncHudLocked();
  }

  // Lock or unlock the NO HUD control. Combat scenarios lock it: NO HUD is fixed
  // at launch (it grants the score bonus), so it can't be flipped mid-mission.
  // `value`, when given, resyncs the displayed state to App's live HUD setting.
  setHudLocked(locked, value) {
    this._hudLocked = locked;
    if (value !== undefined) { this.values.hud = value; this._hudRefresh(); }
    this._syncHudLocked();
  }

  /** Reflect a HUD-visibility change made elsewhere, without re-emitting. */
  setHud(value) {
    this.values.hud = value;
    this._hudRefresh();
  }

  _syncHudLocked() {
    this._hudGroup.classList.toggle('is-disabled', !!this._hudLocked);
    this._hudNote.classList.toggle('is-hidden', !this._hudLocked);
    if (this._hudLocked) {
      this._hudNote.textContent = 'HUD is locked during combat missions.';
    }
  }

  _fmtSens(v) { return `${v.toFixed(2)}×`; }

  // ── Controls ────────────────────────────────────────────────────────────────
  // The control scheme and what tunes it. Rows that only mean something for
  // some schemes (invert, deadzone, device, joystick axes) hide for the rest.
  // Device status is polled while the overlay is open, because browsers only
  // reveal a gamepad once one of its buttons has been pressed.
  _buildControls() {
    const section = this._section('Controls');

    const scheme = this._segRow(section, 'Control scheme',
      SCHEME_ORDER.map((k) => ({ value: k, label: SCHEME_INFO[k].label })),
      () => this.values.controlScheme,
      (v) => { this._emit('controlScheme', v); this._syncControls(); });
    scheme.group.classList.add('btn-group--full');
    this._schemeNote = el('div', 'settings-note settings-note--static');
    section.appendChild(this._schemeNote);

    // Look sensitivity, and the virtual stick's travel in Manual (mouse).
    this._sliderRow(section, 'Mouse sensitivity', 'mouseSensitivity', {
      min: SETTINGS.mouseSensitivityMin,
      max: SETTINGS.mouseSensitivityMax,
      step: SETTINGS.mouseSensitivityStep,
      format: (v) => this._fmtSens(v),
    });

    this._invertRow = this._segRow(section, 'Invert pitch',
      [{ value: false, label: 'Off' }, { value: true, label: 'On' }],
      () => this.values.invertPitch,
      (v) => this._emit('invertPitch', v)).row;

    this._deadzoneRow = this._sliderRow(section, 'Stick deadzone', 'stickDeadzone', {
      min: SETTINGS.stickDeadzoneMin,
      max: SETTINGS.stickDeadzoneMax,
      step: SETTINGS.stickDeadzoneStep,
      format: (v) => `${Math.round(v * 100)}%`,
    }).row;

    // Which device the scheme has found (or a prompt to wake it).
    this._deviceRow = el('div', 'settings-row');
    this._deviceRow.appendChild(el('span', 'settings-label', 'Device'));
    this._deviceStatus = el('span', 'settings-status');
    this._deviceRow.appendChild(this._deviceStatus);
    section.appendChild(this._deviceRow);

    this._buildJoystickAxes(section);
    this._buildFullscreen(section);
    this._syncControls();
  }

  // Joystick axis assignment. A flight stick has no standard mapping, so the
  // browser default may put the twist or slider on the wrong axis — this walks
  // the player through moving each control and records what moved.
  _buildJoystickAxes(section) {
    this._axesRow = el('div', 'settings-row');
    this._axesRow.appendChild(el('span', 'settings-label', 'Joystick axes'));
    const btns = el('div', 'settings-btns');
    const assign = el('button', 'settings-btn', 'Assign');
    assign.type = 'button';
    assign.addEventListener('click', () => this._startCalibration());
    const reset = el('button', 'settings-btn', 'Default');
    reset.type = 'button';
    reset.addEventListener('click', () => { this._cancelCalibration(); this._emit('joystickAxes', null); this._syncAxesNote(); });
    btns.append(assign, reset);
    this._axesRow.appendChild(btns);
    section.appendChild(this._axesRow);

    this._calib = el('div', 'settings-calib is-hidden');
    this._calibText = el('span');
    const calibBtns = el('div', 'settings-btns');
    this._calibSkip = el('button', 'settings-btn', 'Skip');
    this._calibSkip.type = 'button';
    this._calibSkip.addEventListener('click', () => this._calibrator?.skip());
    const cancel = el('button', 'settings-btn', 'Cancel');
    cancel.type = 'button';
    cancel.addEventListener('click', () => this._cancelCalibration());
    calibBtns.append(this._calibSkip, cancel);
    this._calib.append(this._calibText, calibBtns);
    section.appendChild(this._calib);

    this._axesNote = el('div', 'settings-note');
    section.appendChild(this._axesNote);
  }

  _startCalibration() {
    this._cancelCalibration();
    this._calib.classList.remove('is-hidden');
    this._calibrator = new AxisCalibrator({
      onStep: (step, i, n) => {
        this._calibText.textContent = `${i + 1}/${n} — ${step.prompt}`;
        this._calibSkip.style.display = step.optional ? '' : 'none';
      },
      onDone: (layout) => {
        this._calibrator = null;
        this._calib.classList.add('is-hidden');
        if (layout) this._emit('joystickAxes', layout);
        this._syncAxesNote(layout ? null : 'Assignment incomplete — kept the previous axes.');
      },
      onError: (msg) => {
        this._calibrator = null;
        this._calib.classList.add('is-hidden');
        this._syncAxesNote(msg);
      },
    });
    this._calibrator.start();
  }

  _cancelCalibration() {
    this._calibrator?.cancel();
    this._calibrator = null;
    this._calib?.classList.add('is-hidden');
  }

  _syncAxesNote(message = null) {
    this._axesNote.classList.remove('is-hidden');
    this._axesNote.textContent = message
      || (this.values.joystickAxes
        ? 'Using your assigned axes.'
        : 'Using the default layout (Logitech Extreme 3D Pro). Assign axes if the twist or slider reads wrong.');
  }

  // Fullscreen with keyboard lock: the only way Shift/Ctrl can safely work the
  // throttle (Ctrl+W would otherwise close the tab mid-turn). Needs a click to
  // start, so it's a button rather than a saved setting.
  _buildFullscreen(section) {
    const row = el('div', 'settings-row');
    row.appendChild(el('span', 'settings-label', 'Fullscreen'));
    const btn = el('button', 'settings-btn', '');
    btn.type = 'button';
    btn.addEventListener('click', () => toggleFullscreen());
    row.appendChild(btn);
    section.appendChild(row);
    const sync = () => { btn.textContent = isFullscreen() ? 'Exit fullscreen' : 'Enter fullscreen'; };
    sync();
    onKeyboardLockChange(sync);
    section.appendChild(el('div', 'settings-note settings-note--static',
      keyboardLockSupported()
        ? 'In fullscreen the game also captures browser shortcuts, so Shift / Ctrl work the throttle. Hold Esc to leave.'
        : 'Shift / Ctrl throttle needs fullscreen in Chrome or Edge. The scroll wheel always works.'));
  }

  // Show/hide the scheme-specific rows and refresh the scheme description.
  _syncControls() {
    const scheme = this.values.controlScheme;
    const info = SCHEME_INFO[scheme] || SCHEME_INFO.basic;
    this._schemeNote.textContent = `${info.name}. ${info.blurb}`;
    const device = info.device;
    this._invertRow.classList.toggle('is-hidden', !(scheme === 'mouse' || scheme === 'gamepad'));
    this._deadzoneRow.classList.toggle('is-hidden', !device);
    this._deviceRow.classList.toggle('is-hidden', !device);
    this._axesRow.classList.toggle('is-hidden', device !== 'joystick');
    if (device !== 'joystick') { this._cancelCalibration(); this._axesNote.classList.add('is-hidden'); }
    else this._syncAxesNote();
    this._refreshDevice();
  }

  _refreshDevice() {
    const kind = (SCHEME_INFO[this.values.controlScheme] || SCHEME_INFO.basic).device;
    if (!kind) return;
    const pad = findDevice(kind);
    this._deviceStatus.textContent = pad ? deviceLabel(pad) : `Press any button on your ${kind}`;
    this._deviceStatus.classList.toggle('is-missing', !pad);
  }

  // ── Audio ─────────────────────────────────────────────────────────────────
  // Master scales every voice at the audio listener's gain; the rest are
  // per-channel trims applied on top of it. All three of those channels stream
  // through plain HTMLAudioElements, outside the listener's graph — see
  // MusicPlayer, JTACRadio, and CaptionPlayer.
  _buildAudio() {
    const section = this._section('Audio');

    this._volumeRow(section, 'Master volume', 'masterVolume');
    this._volumeRow(section, 'Music volume', 'musicVolume');
    this._volumeRow(section, 'Radio volume', 'jtacVolume');
    this._volumeRow(section, 'Voice volume', 'voiceVolume');

    section.appendChild(el('div', 'settings-note settings-note--static',
      'Music, radio, and instructor voice levels are relative to the master volume.'));
  }

  _volumeRow(parent, label, key) {
    return this._sliderRow(parent, label, key, {
      min: 0,
      max: 1,
      step: SETTINGS.volumeStep,
      format: (v) => this._fmtVol(v),
    });
  }

  _fmtVol(v) { return `${Math.round(v * 100)}%`; }

  // ── Graphics ──────────────────────────────────────────────────────────────
  _buildGraphics() {
    const section = this._section('Graphics');

    // Render scaling — 2× supersampling makes MSAA redundant, so it disables AA.
    this._segRow(section, 'Render scaling',
      SETTINGS.renderScaleOptions.map((s) => ({ value: s, label: `${s}×` })),
      () => this.values.renderScale,
      (v) => { this._emit('renderScale', v); this._syncAAEnabled(); });

    // Post-processing — toggling also enables/disables the AA group.
    this._segRow(section, 'Post-processing',
      [{ value: false, label: 'Off' }, { value: true, label: 'On' }],
      () => this.values.postProcessing,
      (v) => { this._emit('postProcessing', v); this._syncAAEnabled(); });

    // Anti-aliasing — only meaningful while post-processing is on.
    const aa = this._segRow(section, 'Anti-aliasing',
      [{ value: 'off', label: 'Off' }, { value: 'msaa', label: 'MSAA' }],
      () => this.values.antialiasing,
      (v) => this._emit('antialiasing', v));
    this._aaGroup = aa.group;
    this._aaNote = el('div', 'settings-note');
    section.appendChild(this._aaNote);
    this._syncAAEnabled();

    // Shadow resolution — sunLight shadow map size.
    this._segRow(section, 'Shadow resolution',
      SETTINGS.shadowResolutionOptions.map((s) => ({ value: s, label: String(s) })),
      () => this.values.shadowResolution,
      (v) => this._emit('shadowResolution', v));

    // Cloud quality — how finely the volumetric deck is raymarched (resolution,
    // step budget, noise octaves, blur, terrain cloud-shadow samples). Cost only;
    // the weather itself looks the same at every level. Off is not a level but a
    // master switch: it skips the pass outright (see App.setCloudQuality) and
    // takes the start screen's coverage slider with it.
    this._segRow(section, 'Cloud quality',
      SETTINGS.cloudQualityOptions.map((q) => ({ value: q, label: this._fmtQuality(q) })),
      () => this.values.cloudQuality,
      (v) => this._emit('cloudQuality', v));

    section.appendChild(el('div', 'settings-note settings-note--static',
      'Clouds are the heaviest effect, disable them first if the game is running slow.'));
  }

  _fmtQuality(q) { return q.charAt(0).toUpperCase() + q.slice(1); }

  // Grey out / re-enable the AA control. Antialiasing is handled automatically
  // (so the manual control is disabled) when post-processing is off — the MSAA
  // target stands in for renderer AA — or at 2× render scale, where
  // supersampling already antialiases.
  _syncAAEnabled() {
    const superSample = this.values.renderScale >= 2;
    const enabled = this.values.postProcessing && !superSample;
    this._aaGroup.classList.toggle('is-disabled', !enabled);
    this._aaNote.classList.toggle('is-hidden', enabled);
    if (!enabled) {
      this._aaNote.textContent = superSample
        ? 'Antialiasing is handled by 2× supersampling.'
        : 'Antialiasing stays on automatically while post-processing is off.';
    }
  }

  // ── Data ──────────────────────────────────────────────────────────────────
  // Saved-progress management: a single destructive action to wipe everything
  // this game keeps in localStorage — the per-mission scores/grades/stats and
  // the settings above — gated behind an "Are you sure?" confirm.
  _buildData() {
    const section = this._section('Data');

    const row = el('div', 'settings-row');
    row.appendChild(el('span', 'settings-label', 'Saved data'));
    const btn = el('button', 'settings-btn settings-btn--danger', 'Clear saved data');
    btn.type = 'button';
    btn.addEventListener('click', () => this._showConfirm());
    row.appendChild(btn);
    section.appendChild(row);

    section.appendChild(el('div', 'settings-note settings-note--static',
      'Removes saved scores, grades, and stats for every mission, and restores '
      + 'every setting above to its default. This cannot be undone.'));
  }

  /**
   * Restore every persisted setting to its default, emitting each change so App
   * applies it live. Applying re-persists, so UIManager wipes the store after
   * calling this. The HUD toggle is skipped while it's locked — combat arms NO
   * HUD at launch as a scored modifier, and a reset must not unpick it midair.
   */
  resetToDefaults() {
    for (const key of PERSISTED_KEYS) {
      if (key === 'hud' && this._hudLocked) continue;
      if (this.values[key] !== SETTINGS[key]) this._emit(key, SETTINGS[key]);
    }
    this._refreshers.forEach((refresh) => refresh());
    this._syncAAEnabled();
    this._syncControls();
  }

  // A self-contained "Are you sure?" confirm dialog layered over the settings
  // card (kept inside this overlay's element so it lives and dies with it).
  _buildConfirm() {
    this._confirm = el('div', 'confirm-layer is-hidden');

    const backdrop = el('div', 'confirm-backdrop');
    backdrop.addEventListener('click', () => this._hideConfirm());
    this._confirm.appendChild(backdrop);

    const card = el('div', 'confirm-card');
    card.appendChild(el('div', 'confirm-title', 'Clear saved data?'));
    card.appendChild(el('div', 'confirm-text',
      'All saved scores, grades, and stats will be permanently deleted, and every '
      + 'setting will return to its default. This cannot be undone.'));

    const actions = el('div', 'confirm-actions');
    const cancel = el('button', 'settings-btn', 'Cancel');
    cancel.type = 'button';
    cancel.addEventListener('click', () => this._hideConfirm());
    const confirm = el('button', 'settings-btn settings-btn--danger', 'Clear Data');
    confirm.type = 'button';
    confirm.addEventListener('click', () => {
      this.onClearData?.();
      this._hideConfirm();
    });
    actions.append(cancel, confirm);
    card.appendChild(actions);

    this._confirm.appendChild(card);
    this.el.appendChild(this._confirm);
  }

  _showConfirm() { this._confirm.classList.remove('is-hidden'); }
  _hideConfirm() { this._confirm.classList.add('is-hidden'); }

  // Re-hide the confirm dialog whenever the overlay itself is hidden, so it's
  // never left open across a close/reopen.
  setVisible(v) {
    if (!v) this._hideConfirm?.();
    // Poll the device readout only while it can be seen; never leave an axis
    // assignment running behind a closed overlay.
    clearInterval(this._devicePoll);
    if (v) {
      this._refreshDevice?.();
      this._devicePoll = setInterval(() => this._refreshDevice(), DEVICE_POLL_MS);
    } else {
      this._cancelCalibration?.();
    }
    super.setVisible(v);
  }

  // ── Building blocks ─────────────────────────────────────────────────────────
  _section(title) {
    const section = el('div', 'settings-section');
    section.appendChild(el('div', 'settings-section-title', title));
    this.body.appendChild(section);
    return section;
  }

  /**
   * A labeled row containing a range slider and its live readout. Emits the
   * setting on every `input` and registers a refresher so resetToDefaults()
   * can push a new value back into the DOM. Returns `{ row, slider, refresh }`.
   */
  _sliderRow(parent, label, key, { min, max, step, format }) {
    const row = el('div', 'settings-row');
    row.appendChild(el('span', 'settings-label', label));

    const ctrl = el('div', 'settings-slider');
    const slider = el('input');
    slider.type = 'range';
    slider.min = String(min);
    slider.max = String(max);
    slider.step = String(step);
    slider.value = String(this.values[key]);
    const readout = el('span', 'settings-readout', format(this.values[key]));
    slider.addEventListener('input', () => {
      const v = parseFloat(slider.value);
      readout.textContent = format(v);
      this._emit(key, v);
    });

    const refresh = () => {
      slider.value = String(this.values[key]);
      readout.textContent = format(this.values[key]);
    };
    this._refreshers.push(refresh);

    ctrl.append(slider, readout);
    row.appendChild(ctrl);
    parent.appendChild(row);
    return { row, slider, refresh };
  }

  /**
   * A labeled row containing a segmented button group. Returns
   * `{ row, group, refresh }`.
   */
  _segRow(parent, label, items, getActive, onPick) {
    const row = el('div', 'settings-row');
    row.appendChild(el('span', 'settings-label', label));
    const group = el('div', 'btn-group');

    const buttons = items.map((it) => {
      const b = el('button', 'seg-btn', it.label);
      b.type = 'button';
      b._value = it.value;
      b.addEventListener('click', () => { onPick(it.value); refresh(); });
      group.appendChild(b);
      return b;
    });
    const refresh = () => buttons.forEach((b) => b.classList.toggle('is-active', b._value === getActive()));
    this._refreshers.push(refresh);
    refresh();

    row.appendChild(group);
    parent.appendChild(row);
    return { row, group, refresh };
  }
}
